/**
 * product-card-slider.js
 *
 * Initialises per-variant image groups on product cards.
 * When a color swatch is clicked the matching group's Swiper is shown
 * and all others are hidden — giving each swatch its own image set.
 *
 * Falls back gracefully to a single shared Swiper when no variant groups exist.
 */
(function () {
    'use strict';

    /** Build a Swiper config bound to a specific group container */
    function buildSwiperConfig(groupEl) {
        return {
            slidesPerView: 1,
            loop: false,
            grabCursor: true,
            watchOverflow: true,
            threshold: 5,
            navigation: {
                nextEl: groupEl.querySelector('.card-slider-next'),
                prevEl: groupEl.querySelector('.card-slider-prev'),
            },
            pagination: {
                el: groupEl.querySelector('.card-slider-pagination'),
                clickable: true,
                dynamicBullets: false,
            },
            autoplay: false,
            speed: 350,
            effect: 'slide',
            a11y: {
                enabled: true,
                prevSlideMessage: 'Previous image',
                nextSlideMessage: 'Next image',
            },
        };
    }

    /**
     * Initialise the card slider inside a .pro-img container.
     * Supports both the new per-variant group layout and the old single-swiper fallback.
     */
    function initCardSlider(imgContainer) {
        if (imgContainer._cardSliderInitialised) return;
        imgContainer._cardSliderInitialised = true;

        const slideCount = parseInt(imgContainer.dataset.slideCount, 10) || 1;
        if (slideCount <= 1) return;
        if (typeof Swiper === 'undefined') return;

        const card = imgContainer.closest('.single-product-wrap');
        if (!card) return;

        // ── Per-variant group layout ──────────────────────────────────────────
        const groups = imgContainer.querySelectorAll('.card-slider-group');

        if (groups.length > 0) {
            // Initialise a Swiper inside each group that contains a .card-slider
            const swipers = [];
            groups.forEach(function (group, idx) {
                const swiperEl = group.querySelector('.card-slider');
                if (!swiperEl) {
                    swipers.push(null);
                    return;
                }
                const swiper = new Swiper(swiperEl, buildSwiperConfig(group));
                swipers.push(swiper);
                group._swiperInstance = swiper;
            });

            // Store on container for external access
            imgContainer._swipers = swipers;
            imgContainer._activeGroupIndex = 0;

            // Force-load images in the initially-visible group
            var firstGroup = imgContainer.querySelector('.card-slider-group.active-group');
            if (firstGroup) {
                firstGroup.querySelectorAll('img').forEach(function (img) {
                    var isLazy = false;
                    if (img.hasAttribute('loading')) { img.removeAttribute('loading'); isLazy = true; }
                    var dataSrc = img.getAttribute('data-src');
                    var dataSrcset = img.getAttribute('data-srcset');
                    if (dataSrc) { img.setAttribute('src', dataSrc); img.removeAttribute('data-src'); isLazy = true; }
                    if (dataSrcset) { img.setAttribute('srcset', dataSrcset); img.removeAttribute('data-srcset'); isLazy = true; }
                    
                    // Only trigger a repaint if we actually changed lazy states
                    if (isLazy) {
                        if (img.srcset) img.srcset = img.srcset;
                        else if (img.src) img.src = img.src;
                    }
                    img.classList.remove('lazyload', 'lazyloading');
                });
            }

            // ── Connect color swatches ──
            const swatches = card.querySelectorAll('.item-data');

            swatches.forEach(function (swatch) {
                swatch.addEventListener('click', function (e) {
                    e.preventDefault();
                    e.stopPropagation();

                    const targetIdx = parseInt(swatch.dataset.variantIndex, 10);
                    if (isNaN(targetIdx)) return;

                    // Hide all groups, show target
                    groups.forEach(function (g) {
                        g.style.display = 'none';
                        g.classList.remove('active-group');
                    });
                    const targetGroup = imgContainer.querySelector(
                        '.card-slider-group[data-variant-group="' + targetIdx + '"]'
                    );
                    if (targetGroup) {
                        targetGroup.style.display = 'block';
                        targetGroup.classList.add('active-group');

                        // Force images inside the newly-visible group to load.
                        targetGroup.querySelectorAll('img').forEach(function (img) {
                            var isLazy = false;
                            if (img.hasAttribute('loading')) { img.removeAttribute('loading'); isLazy = true; }
                            var dataSrc = img.getAttribute('data-src');
                            var dataSrcset = img.getAttribute('data-srcset');
                            if (dataSrc) { img.setAttribute('src', dataSrc); img.removeAttribute('data-src'); isLazy = true; }
                            if (dataSrcset) { img.setAttribute('srcset', dataSrcset); img.removeAttribute('data-srcset'); isLazy = true; }
                            
                            // Only trigger a repaint if we actually changed lazy states
                            if (isLazy) {
                                if (img.srcset) img.srcset = img.srcset;
                                else if (img.src) img.src = img.src;
                            }
                            img.classList.remove('lazyload', 'lazyloading');
                        });

                        // Reset to first slide on group switch
                        if (targetGroup._swiperInstance) {
                            targetGroup._swiperInstance.slideTo(0, 0);
                            targetGroup._swiperInstance.update();
                        }
                    }

                    imgContainer._activeGroupIndex = targetIdx;

                    // Update active swatch visual
                    card.querySelectorAll('.variant-option').forEach(function (v) {
                        v.classList.remove('active-variant');
                    });
                    const swatchWrap = swatch.closest('.variant-option');
                    if (swatchWrap) swatchWrap.classList.add('active-variant');
                });
            });

            // Desktop hover — go to slide 1 in the active group on card hover
            const canHover = window.matchMedia('(hover: hover)').matches;
            if (canHover) {
                card.addEventListener('mouseenter', function () {
                    const activeGroup = imgContainer.querySelector('.card-slider-group.active-group');
                    if (activeGroup && activeGroup._swiperInstance) {
                        activeGroup._swiperInstance.slideTo(1);
                    }
                });
            }

            return; // done — variant group path
        }

        // ── Legacy single-swiper fallback ─────────────────────────────────────
        const swiperEl = imgContainer.querySelector('.card-slider');
        if (!swiperEl) return;

        const swiper = new Swiper(swiperEl, buildSwiperConfig(imgContainer));
        imgContainer._cardSliderInstance = swiper;

        // Connect swatches to slide index
        const swatches = card.querySelectorAll('.item-data');
        if (swatches.length > 0) {
            swatches.forEach(function (swatch, index) {
                swatch.addEventListener('click', function (e) {
                    e.preventDefault();
                    e.stopPropagation();
                    if (imgContainer._cardSliderInstance && !imgContainer._isSliding) {
                        imgContainer._isClickingSwatch = true;
                        imgContainer._cardSliderInstance.slideTo(index);
                        setTimeout(function () { imgContainer._isClickingSwatch = false; }, 50);
                    }
                    // Update active swatch visual
                    card.querySelectorAll('.variant-option').forEach(function (v) {
                        v.classList.remove('active-variant');
                    });
                    const swatchWrap = swatch.closest('.variant-option');
                    if (swatchWrap) swatchWrap.classList.add('active-variant');
                });
            });

            swiper.on('slideChange', function () {
                if (imgContainer._isClickingSwatch) return;
                const activeIndex = swiper.activeIndex;
                const targetSwatch = swatches[activeIndex];
                if (targetSwatch) {
                    imgContainer._isSliding = true;
                    swatches.forEach(function (s) {
                        const optionWrap = s.closest('.variant-option');
                        if (optionWrap) optionWrap.classList.remove('active-variant');
                    });
                    const optionWrap = targetSwatch.closest('.variant-option');
                    if (optionWrap) optionWrap.classList.add('active-variant');
                    setTimeout(function () { imgContainer._isSliding = false; }, 50);
                }
            });
        }

        const canHover = window.matchMedia('(hover: hover)').matches;
        if (canHover) {
            card.addEventListener('mouseenter', function () {
                if (imgContainer._cardSliderInstance) {
                    imgContainer._cardSliderInstance.slideTo(1);
                }
            });
        }
    }

    /** Observe containers and lazily init sliders as they enter the viewport */
    function observeContainers(containers) {
        if (!containers || !containers.length) return;
        if ('IntersectionObserver' in window) {
            const io = new IntersectionObserver(
                function (entries) {
                    entries.forEach(function (entry) {
                        if (entry.isIntersecting) {
                            initCardSlider(entry.target);
                            io.unobserve(entry.target);
                        }
                    });
                },
                { rootMargin: '200px 0px', threshold: 0 }
            );
            containers.forEach(function (el) { io.observe(el); });
        } else {
            containers.forEach(initCardSlider);
        }
    }

    function boot() {
        const containers = document.querySelectorAll('.pro-img[data-card-slider]');
        observeContainers(Array.from(containers));
    }

    /** Watch for dynamically injected cards (AJAX filters, related products, etc.) */
    function watchForNewCards() {
        if (!('MutationObserver' in window)) return;
        const mo = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                mutation.addedNodes.forEach(function (node) {
                    if (node.nodeType !== 1) return;
                    if (node.matches && node.matches('.pro-img[data-card-slider]')) {
                        observeContainers([node]);
                    }
                    const nested = node.querySelectorAll
                        ? node.querySelectorAll('.pro-img[data-card-slider]')
                        : [];
                    observeContainers(Array.from(nested));
                });
            });
        });
        mo.observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            boot();
            watchForNewCards();
        });
    } else {
        boot();
        watchForNewCards();
    }
})();
